//get all employees

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { toast } from 'react-toastify';
import {
  getOrders,
  createOrder,
  updateOrder,
  deleteOrder,
  OrderFormData,
  IOrder,
  approveOrder,
  getOrderByUser,
  sendToManagerMarketing,
  IItemsOrder,
  getOrderStatistics,
  getCustomerOrderStatistics,
  getTotalOrderStatistics,
  IOrderStatistics,
  ICustomerOrderStatistics,
  ITotalOrderStatistics,
} from '../api/orders';
import { IEmployee } from '../api/employee';
import { ICustomer } from '../api/customers';
import { OrderStatus } from '../api/orders';
import { DateRange } from 'react-day-picker';

export interface IChangeStatusOrder {
  id: string;
  status: string;
  reasonReject?: string;
}
// DTO cho customer
export interface OrderDTO {
  id: string;
  customer: ICustomer;
  employee: IEmployee;
  color: string;
  status: OrderStatus;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  createdBy: IEmployee;
  budgetMaxBuyGift: number;
  sellDate: string;
  reasonReject: string;
  explanation: string;
  items: IItemsOrder[];
  amountToBePaid: number;
}

// Chuyển đổi từ IOrderResponse sang OrderDTO
const parseOrderDTO = (order: IOrder): OrderDTO => ({
  id: order.id,
  customer: order.customer,
  employee: order.employee,
  color: order.color,
  status: order.status,
  createdAt: order.createdAt,
  updatedAt: order.updatedAt,
  deletedAt: order.deletedAt,
  createdBy: order.createdBy,
  budgetMaxBuyGift: order.budgetMaxBuyGift,
  sellDate: order.sellDate,
  reasonReject: order.reasonReject || '',
  explanation: order.explanation || '',
  items: order.items.sort((a, b) => Number(a.id) - Number(b.id)),
  amountToBePaid: parseFloat(order.amountToBePaid.toString()),
});

// Lấy danh sách orders (có phân trang, tìm kiếm)
export const useOrders = ({
  page,
  limit,
  search,
  customerId,
  employeeId,
  priceSearch,
  dateRange,
  statusSearch,
}: {
  page: number;
  limit: number;
  search: string;
  customerId: string | null;
  employeeId: string | null;
  priceSearch: string | null;
  dateRange: DateRange | undefined;
  statusSearch: string | null;
}) => {
  return useQuery<{
    orders: OrderDTO[];
    total: number;
    pages: number;
    hasNextPage: boolean;
  }>({
    queryKey: [
      'orders',
      page,
      limit,
      search,
      customerId,
      employeeId,
      priceSearch,
      dateRange?.from && dateRange?.to,
      statusSearch,
    ],
    queryFn: async () => {
      const response = await getOrders(
        page,
        limit,
        search,
        customerId,
        employeeId,
        priceSearch,
        dateRange,
        statusSearch
      );
      const orders = response.items.map(parseOrderDTO);
      return {
        orders,
        total: response.totalItems,
        pages: response.totalPages,
        hasNextPage: response.currentPage < response.totalPages,
      };
    },
    placeholderData: (previousData) => previousData,
  });
};
// Tạo order
export const useCreateOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: OrderFormData) => {
      const response = await createOrder(data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast.success('Tạo đơn hàng thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// Cập nhật order
export const useUpdateOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: OrderFormData }) => {
      const response = await updateOrder(id, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast.success('Cập nhật đơn hàng thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// Xóa order
export const useDeleteOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await deleteOrder(id);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast.success('Xóa đơn hàng thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useApproveOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: IChangeStatusOrder) => {
      const response = await approveOrder(
        data.id,
        data.status,
        data.reasonReject
      );
      return response;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ['orders'] });
      if (
        data.status === OrderStatus.ADMIN_APPROVED ||
        data.status === OrderStatus.GENERAL_DIRECTOR_APPROVED ||
        data.status === OrderStatus.BUSINESS_APPROVED ||
        data.status === OrderStatus.ACCOUNTING_APPROVED ||
        data.status === OrderStatus.APPROVED
      ) {
        toast.success('Duyệt đơn hàng thành công');
      } else {
        toast.success('Từ chối đơn hàng thành công');
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

export const useGetOrderByUser = () => {
  return useQuery({
    queryKey: ['order-by-user'],
    queryFn: async () => {
      const response = await getOrderByUser();
      return response;
    },
  });
};

export const useSendToManagerMarketing = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await sendToManagerMarketing(id);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast.success('Gửi đến quản lý marketing thành công');
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
};

// Order Statistics Hook
export const useOrderStatistics = (
  employeeId: number | undefined,
  year: number
) => {
  return useQuery<IOrderStatistics>({
    queryKey: ['orderStatistics', employeeId, year],
    queryFn: async () => {
      if (!employeeId) {
        throw new Error('Employee ID is required');
      }
      return await getOrderStatistics(employeeId, year);
    },
    enabled: !!employeeId && !!year,
  });
};

export const useCustomerOrderStatistics = (
  customerId?: number,
  year?: number
) => {
  return useQuery<ICustomerOrderStatistics>({
    queryKey: ['customer-order-statistics', customerId, year],
    queryFn: async () => {
      if (!customerId || !year) {
        throw new Error('Customer ID and Year are required');
      }
      return await getCustomerOrderStatistics(customerId, year);
    },
    enabled: !!customerId && !!year,
  });
};

// Total Order Statistics Hook
export const useTotalOrderStatistics = (year: number) => {
  return useQuery<ITotalOrderStatistics>({
    queryKey: ['total-order-statistics', year],
    queryFn: async () => {
      return await getTotalOrderStatistics(year);
    },
    enabled: !!year,
  });
};
